import { useRef, useEffect, useCallback } from 'react'
import type { SchemaField } from '../../../../types/schede'
import FieldWrapper from './FieldWrapper'

interface SignatureFieldProps {
  field: SchemaField
  value: any
  onChange: (value: any) => void
  readOnly?: boolean
  error?: string
}

export default function SignatureField({ field, value, onChange, readOnly, error }: SignatureFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isDrawingRef = useRef(false)

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#000000'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [])

  useEffect(() => {
    if (!readOnly) {
      initCanvas()
    }
  }, [readOnly, initCanvas])

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    if ('touches' in e) {
      const touch = e.touches[0]
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top }
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top }
  }

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (readOnly) return
    isDrawingRef.current = true
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx) return
    const pos = getPos(e)
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
  }

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawingRef.current || readOnly) return
    e.preventDefault()
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx) return
    const pos = getPos(e)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
  }

  const stopDrawing = () => {
    if (!isDrawingRef.current) return
    isDrawingRef.current = false
    const canvas = canvasRef.current
    if (!canvas) return
    const dataUrl = canvas.toDataURL('image/png')
    onChange(dataUrl)
  }

  const clearCanvas = () => {
    initCanvas()
    onChange(null)
  }

  // Read-only mode: show signature image
  if (readOnly) {
    return (
      <FieldWrapper field={field} error={error}>
        {value ? (
          <img
            src={value}
            alt="Firma"
            className="border rounded"
            style={{
              borderColor: 'var(--color-border)',
              maxWidth: 300,
              maxHeight: 150,
              backgroundColor: '#ffffff',
            }}
          />
        ) : (
          <div
            className="text-xs px-2 py-3 border rounded text-center"
            style={{
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            Nessuna firma
          </div>
        )}
      </FieldWrapper>
    )
  }

  // Show existing signature as preview if present
  return (
    <FieldWrapper field={field} error={error}>
      {value && (
        <div className="mb-1">
          <img
            src={value}
            alt="Firma corrente"
            className="border rounded"
            style={{
              borderColor: 'var(--color-border)',
              maxWidth: 300,
              maxHeight: 150,
              backgroundColor: '#ffffff',
            }}
          />
        </div>
      )}
      <canvas
        ref={canvasRef}
        width={300}
        height={150}
        className="border rounded cursor-crosshair"
        style={{
          borderColor: 'var(--color-border)',
          backgroundColor: '#ffffff',
          touchAction: 'none',
        }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      <button
        type="button"
        onClick={clearCanvas}
        className="mt-1 px-2 py-0.5 text-xs border rounded hover:opacity-80"
        style={{
          borderColor: 'var(--color-border)',
          color: 'var(--color-text-secondary)',
          backgroundColor: 'var(--color-bg-card)',
        }}
      >
        Cancella
      </button>
    </FieldWrapper>
  )
}
